import { HttpClient } from '@angular/common/http'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  Signal,
  computed,
  effect,
  inject,
  input,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { GridApi, GridOptions, ICellRendererParams } from 'ag-grid-community'

import {
  DefaultActionsColumn,
  FlxResourcesGridComponent,
  getActionsColumnLength,
} from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { FlxFileSizePipe } from '@flowx/designer/pipes/size.pipe'
import { ActionsCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/actions-cell/actions-cell.component'
import { FlxDateCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/date-cell.component'
import {
  DocumentSourceLabels,
  InstanceDocument,
} from '@flowx/designer/shared/entities/instance-document.types'
import { ProcessInstance } from '@flowx/designer/shared/entities/process-instance.types'
import { openBlobInNewTab } from '@flowx/shared/utils'

import { InstanceDocumentsService } from './instance-documents.service'

const fileSizePipe = new FlxFileSizePipe()

@Component({
  selector: 'flx-instance-documents',
  templateUrl: './instance-documents.component.html',
  imports: [FlxResourcesGridComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [InstanceDocumentsService],
})
export class InstanceDocumentsComponent implements OnDestroy {
  #documentsService = inject(InstanceDocumentsService)
  #http = inject(HttpClient)
  #destroyRef = inject(DestroyRef)
  #baseUrl = inject<string>('BASE_URL' as any)

  processInstanceId = input<ProcessInstance['id']>(null)

  gridOptions: Signal<GridOptions> = computed(() => ({
    rowModelType: 'infinite',
    datasource: this.#documentsService.gridDataSource(),
  }))

  actions = [
    {
      icon: 'ph-arrow-square-out',
      tooltip: 'Preview',
      clickAction: (doc: InstanceDocument): void => {
        this.#http
          .get(`${this.#baseUrl}/document/${doc.downloadPath}`, {
            responseType: 'blob',
            headers: { Accept: doc.mimeType || 'application/octet-stream' },
          })
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe((blob) => {
            openBlobInNewTab(blob, doc.fileName)
          })
      },
    },
  ]

  readonly documentsTableColumns = [
    { headerName: 'File Name', field: 'fileName', minWidth: 150 },
    {
      headerName: 'File Size',
      field: 'fileSize',
      flex: 0,
      width: 120,
      valueGetter: (params: ICellRendererParams): string =>
        params.data?.fileSize ? fileSizePipe.transform(params.data?.fileSize) : '-',
    },
    {
      headerName: 'Source',
      field: 'source',
      flex: 0,
      width: 160,
      valueGetter: (params: ICellRendererParams): string =>
        params.data?.source ? DocumentSourceLabels[params.data?.source] : '-',
    },
    { headerName: 'Uploaded By', field: 'uploadedBy', minWidth: 150 },
    {
      headerName: 'Uploaded At',
      field: 'uploadedAt',
      cellRenderer: FlxDateCellComponent,
      flex: 0,
      width: 220,
    },
    {
      ...DefaultActionsColumn,
      width: getActionsColumnLength(this.actions.length),
      cellRenderer: ActionsCellComponent<InstanceDocument>,
      cellRendererParams: {
        actions: this.actions,
      },
    },
  ]

  constructor() {
    effect(() => {
      const processInstanceId = this.processInstanceId()
      if (processInstanceId) {
        this.#documentsService.setInitialQuery({
          processInstanceId: processInstanceId.toString(),
        })
      }
    })
  }

  onGridReady(gridApi: GridApi): void {
    this.#documentsService.setGridApi(gridApi)
  }

  ngOnDestroy(): void {
    this.#documentsService.reset()
  }
}
